import { Check, ChevronDown, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { NoteKind, SourceKind } from "../types";
import { slugifyStatus, statusMeta, statusOptionsForKind } from "../lib/lifecycle";

type Props = {
  kind: NoteKind | SourceKind;
  status?: string;
  // Extra custom status slugs already used by other nodes of this kind, so they show up as
  // reusable options here too (union with the seeded per-kind enum).
  customStatuses?: string[];
  onChange: (status: string) => void | Promise<void>;
  className?: string;
};

export function StatusPicker({ kind, status, customStatuses = [], onChange, className }: Props) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const current = statusMeta(kind, status);
  const builtinSlugs = statusOptionsForKind(kind).map((option) => option.slug);
  // Built-in options first, then any custom slugs in use (current one included), de-duplicated.
  const extraSlugs = Array.from(new Set([...customStatuses, current.slug])).filter((slug) => !builtinSlugs.includes(slug));
  const options = [...statusOptionsForKind(kind), ...extraSlugs.map((slug) => statusMeta(kind, slug))];

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setAdding(false);
        setDraft("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const apply = async (next: string) => {
    if (!next || next === current.slug) {
      setOpen(false);
      setAdding(false);
      setDraft("");
      return;
    }
    setBusy(true);
    try {
      await onChange(next);
      setOpen(false);
      setAdding(false);
      setDraft("");
    } finally {
      setBusy(false);
    }
  };

  const submitNew = () => {
    const slug = slugifyStatus(draft);
    if (slug) {
      void apply(slug);
    }
  };

  return (
    <div className={`relative ${className ?? ""}`} ref={containerRef}>
      <button
        type="button"
        disabled={busy}
        onClick={() => setOpen((value) => !value)}
        title={`Đổi status (${current.label})`}
        aria-label={`Đổi status, hiện tại ${current.label}`}
        className={`inline-flex h-6 items-center gap-1 rounded-md border px-2 text-xs font-semibold transition hover:brightness-95 disabled:opacity-50 ${current.tone}`}
      >
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: current.dotColor }} />
        {current.label}
        <ChevronDown size={12} aria-hidden="true" className="opacity-60" />
      </button>

      {open ? (
        <div className="absolute right-0 z-20 mt-1 w-52 overflow-hidden rounded-lg border border-line bg-white py-1 shadow-lg">
          <ul className="max-h-60 overflow-auto py-0.5">
            {options.map((option) => (
              <li key={option.slug}>
                <button
                  type="button"
                  onClick={() => void apply(option.slug)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: option.dotColor }} />
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  {option.slug === current.slug ? <Check size={13} className="shrink-0 text-brand" aria-hidden="true" /> : null}
                </button>
              </li>
            ))}
          </ul>

          <div className="mt-0.5 border-t border-line pt-1">
            {adding ? (
              <div className="flex items-center gap-1 px-2 pb-1">
                <input
                  autoFocus
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      submitNew();
                    } else if (event.key === "Escape") {
                      setAdding(false);
                      setDraft("");
                    }
                  }}
                  placeholder="Tên status mới…"
                  className="h-7 min-w-0 flex-1 rounded-md border border-line px-2 text-xs outline-none focus:border-brand"
                />
                <button
                  type="button"
                  onClick={submitNew}
                  disabled={!slugifyStatus(draft)}
                  className="inline-flex h-7 items-center rounded-md bg-brand px-2 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-40"
                >
                  Thêm
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs font-semibold text-brand hover:bg-blue-50"
              >
                <Plus size={13} aria-hidden="true" />
                Thêm status mới…
              </button>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
